import type { Contract } from '@gmn-portal/backend';
import type {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
} from '@tanstack/react-table';
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  Box,
  Building2,
  ChartGantt,
  Circle,
  CircleCheck,
  CircleDot,
  Eye,
  FileText,
  GitBranch,
  MoreHorizontal,
  Plus,
  Table2,
} from 'lucide-react';
import { DateTime } from 'luxon';
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useContracts, useCustomer, useDeleteContract, useObject, useObjects } from '@/core/api';
import { useAuth } from '@/core/auth';
import { useIntl } from '@/core/i18n/use-intl';
import { SearchCombobox } from '@/shared/components/inputs';
import { PageContainer, PageTitle, SimpleBreadcrumbItem } from '@/shared/components/layout';
import {
  SortableHeader,
  TableActiveFilterTags,
  TableCard,
  TableCardContent,
  TableCardFooter,
  TableCardHeader,
  TableClearAllButton,
  TableFiltersBar,
  TableLoadingCell,
  TablePagination,
  TableSearchBar,
} from '@/shared/components/tables';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  EmptyTableRow,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableHeaderRow,
  TableRow,
  Toggle,
  ToggleGroup,
  ToggleGroupItem,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/shared/components/ui';
import { TimelineControls } from '@/shared/components/ui/timeline-controls';
import { useDebounce } from '@/shared/hooks/use-debounce';
import { useTablePreferences } from '@/shared/services/table-preferences';
import {
  canCreateService,
  canDeleteService,
  canReadService,
  canUpdateService,
  resolveUserGroup,
  resolveUserGroupDomain,
} from '@/shared/utils/permissions';
import { getSortParam } from '@/shared/utils/table-utils';

import { ContractsTimeline, type ContractsTimelineHandle } from '../components/contracts-timeline';
import { CopyContractModal } from '../components/copy-contract-modal';

const CustomerCell = ({ customerId }: { customerId: string }) => {
  const { data: customer, isLoading } = useCustomer(customerId);
  if (isLoading) return <Skeleton className="h-4 w-24" />;
  return (
    <span className="text-muted-foreground truncate" title={customer?.name || '-'}>
      {customer?.name || '-'}
    </span>
  );
};

const CustomerFilterLabel = ({ customerId }: { customerId: string }) => {
  const { data: customer, isLoading } = useCustomer(customerId);
  const label = customer?.name || customerId;
  return (
    <span className="truncate max-w-[240px]" title={label}>
      {isLoading ? '...' : label}
    </span>
  );
};

const ObjectFilterLabel = ({ objectId }: { objectId: string }) => {
  const { data: objectData, isLoading } = useObject(objectId);
  const label = objectData?.name || objectId;
  return (
    <span className="truncate max-w-[240px]" title={label}>
      {isLoading ? '...' : label}
    </span>
  );
};

const ObjectCell = ({ objectIds }: { objectIds: string[] }) => {
  const { data: objectsData, isLoading } = useObjects({
    where: {
      id: {
        in: objectIds,
      },
    },
    limit: objectIds.length,
    disabled: !objectIds || objectIds.length === 0,
  });

  if (!objectIds || objectIds.length === 0) {
    return <span className="text-muted-foreground">-</span>;
  }

  if (isLoading) {
    return <Skeleton className="h-4 w-24" />;
  }

  const objects = objectsData?.docs || [];

  if (objects.length === 0) {
    return <span className="text-muted-foreground">-</span>;
  }

  if (objects.length === 1) {
    return (
      <span className="text-muted-foreground truncate" title={objects[0].name}>
        {objects[0].name}
      </span>
    );
  }

  // Show first 2 object names and count if more
  const displayObjects = objects.slice(0, 2);
  const remainingCount = objects.length - displayObjects.length;
  const allObjectNames = objects.map((obj) => obj.name);

  return (
    <Tooltip delayDuration={200}>
      <TooltipTrigger asChild>
        <span className="text-muted-foreground truncate">
          {displayObjects.map((obj) => obj.name).join(', ')}
          {remainingCount > 0 && ` +${remainingCount}`}
        </span>
      </TooltipTrigger>
      <TooltipContent side="bottom" align="start" className="max-w-xs">
        <div className="text-sm">
          {allObjectNames.map((name, index) => (
            <div key={index}>{name}</div>
          ))}
        </div>
      </TooltipContent>
    </Tooltip>
  );
};

const ActionCell = ({
  contract,
  onDelete,
  onCopy,
  onViewDetail,
  basePath,
  canUpdate,
  canCopy,
  canDelete,
}: {
  contract: Contract;
  onDelete: (contract: Contract) => void;
  onCopy: (contract: Contract) => void;
  onViewDetail: (id: string) => void;
  basePath: string;
  canUpdate: boolean;
  canCopy: boolean;
  canDelete: boolean;
}) => {
  const navigate = useNavigate();
  const { t } = useIntl();

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions lint/a11y/useKeyWithClickEvents: Container only stops propagation, not interactive
    <div
      className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity"
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        onClick={() => onViewDetail(contract.id)}
        className="p-1.5 hover:bg-secondary text-primary rounded-md transition-colors"
        title={t('common.view')}
      >
        <Eye size={16} />
      </button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="p-1.5 hover:bg-muted text-muted-foreground rounded-md transition-colors"
          >
            <MoreHorizontal size={16} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel>{t('common.actions')}</DropdownMenuLabel>
          <DropdownMenuItem onClick={() => navigate(`${basePath}/${contract.id}`)}>
            {canUpdate ? t('common.edit') : t('common.view')}
          </DropdownMenuItem>
          {canCopy && (
            <DropdownMenuItem onClick={() => onCopy(contract)}>
              {t('contracts.copyContract')}
            </DropdownMenuItem>
          )}
          {canDelete && (
            <DropdownMenuItem onClick={() => onDelete(contract)}>
              {t('common.delete')}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export function ContractsOverview() {
  const { t } = useIntl();
  const navigate = useNavigate();
  const { user } = useAuth();
  const group = resolveUserGroup(user);
  const canCreateContracts = group ? canCreateService(group, 'contracts') : false;
  const canUpdateContracts = group ? canUpdateService(group, 'contracts') : false;
  const canDeleteContracts = group ? canDeleteService(group, 'contracts') : false;
  const canReadCustomers = group ? canReadService(group, 'customers') : false;
  const canReadObjects = group ? canReadService(group, 'objects') : false;
  const basePath =
    resolveUserGroupDomain(user) === 'customer'
      ? '/c/contract-management/contracts'
      : '/gmn/contract-management/contracts';

  const tablePreferences = useTablePreferences({
    tableId: 'contracts-overview',
    defaultPreferences: {
      pagination: {
        pageIndex: 0,
        pageSize: 10,
      },
      sorting: [],
      columnFilters: [],
      columnVisibility: {},
      view: 'table',
      searchTerm: '',
      selectedFilters: {
        customerId: undefined,
        objectId: undefined,
        status: undefined,
        showSubcontracts: false,
      },
    },
  });

  // Load initial preferences
  const preferences = tablePreferences.loadPreferences();

  const [searchTerm, setSearchTerm] = useState(preferences.searchTerm || '');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | undefined>(
    (preferences.selectedFilters?.customerId as string) || undefined
  );
  const [selectedObjectId, setSelectedObjectId] = useState<string | undefined>(
    (preferences.selectedFilters?.objectId as string) || undefined
  );
  const [selectedStatus, setSelectedStatus] = useState<'open' | 'closed' | undefined>(
    (preferences.selectedFilters?.status as 'open' | 'closed') || undefined
  );
  const [showSubcontracts, setShowSubcontracts] = useState<boolean>(
    Boolean(preferences.selectedFilters?.showSubcontracts)
  );
  const [sorting, setSorting] = useState<SortingState>(preferences.sorting || []);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(
    preferences.columnFilters || []
  );
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    preferences.columnVisibility || {}
  );
  const [rowSelection, setRowSelection] = useState({});
  const [pagination, setPagination] = useState(
    preferences.pagination || {
      pageIndex: 0,
      pageSize: 10,
    }
  );
  const [view, setView] = useState<'table' | 'timeline'>(
    (preferences.view as 'table' | 'timeline') || 'table'
  );

  useEffect(() => {
    if (canReadCustomers || !selectedCustomerId) return;
    setSelectedCustomerId(undefined);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, [canReadCustomers, selectedCustomerId]);

  useEffect(() => {
    if (canReadObjects || !selectedObjectId) return;
    setSelectedObjectId(undefined);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, [canReadObjects, selectedObjectId]);

  // Save preferences when they change
  useEffect(() => {
    tablePreferences.savePreferences({
      pagination,
      sorting,
      columnFilters,
      columnVisibility,
      view,
      searchTerm,
      selectedFilters: {
        customerId: selectedCustomerId,
        objectId: selectedObjectId,
        status: selectedStatus,
        showSubcontracts,
      },
    });
  }, [
    pagination,
    sorting,
    columnFilters,
    columnVisibility,
    view,
    searchTerm,
    selectedCustomerId,
    selectedObjectId,
    selectedStatus,
    showSubcontracts,
  ]);

  const handleViewDetail = useCallback(
    (id: string) => {
      navigate(`${basePath}/${id}`);
    },
    [navigate, basePath]
  );

  const columns: ColumnDef<Contract>[] = [
    {
      accessorKey: 'title',
      header: ({ column }) => <SortableHeader column={column} title={t('contracts.title')} />,
      cell: ({ row }) => {
        const isSubcontract = Boolean((row.original as any).parentContract);
        return (
          <span
            className="font-medium text-foreground truncate flex items-center gap-1"
            title={row.getValue('title')}
          >
            {isSubcontract ? (
              <span className="text-muted-foreground" aria-hidden>
                ↳
              </span>
            ) : null}
            <span className="truncate">{row.getValue('title')}</span>
          </span>
        );
      },
      size: 180,
      minSize: 150,
      maxSize: 250,
    },
    {
      accessorKey: 'orderNumber',
      header: ({ column }) => <SortableHeader column={column} title={t('contracts.orderNumber')} />,
      cell: ({ row }) => (
        <span className="text-muted-foreground" title={row.getValue('orderNumber')}>
          {row.getValue('orderNumber')}
        </span>
      ),
      size: 150,
      minSize: 120,
      maxSize: 200,
    },
    ...(canReadCustomers
      ? [
          {
            accessorKey: 'customer',
            header: ({ column }) => (
              <SortableHeader column={column} title={t('customers.customer')} />
            ),
            cell: ({ row }) => <CustomerCell customerId={row.getValue('customer') as string} />,
            size: 200,
            minSize: 150,
            maxSize: 250,
          } as ColumnDef<Contract>,
        ]
      : []),
    ...(canReadObjects
      ? [
          {
            accessorKey: 'aggregatedObjects',
            header: () => <div className="text-left font-medium">{t('objects.objects')}</div>,
            cell: ({ row }) => {
              const aggregatedObjects = row.getValue('aggregatedObjects') as (
                | string
                | { id: string }
              )[];
              const objectIds =
                aggregatedObjects?.map((obj) => (typeof obj === 'string' ? obj : obj.id)) || [];
              return <ObjectCell objectIds={objectIds} />;
            },
            size: 200,
            minSize: 150,
            maxSize: 250,
          } as ColumnDef<Contract>,
        ]
      : []),
    {
      accessorKey: 'startDate',
      header: ({ column }) => <SortableHeader column={column} title={t('contracts.startDate')} />,
      cell: ({ row }) => {
        const date = row.getValue('startDate') as string;
        return (
          <span className="text-muted-foreground">
            {date ? DateTime.fromISO(date).toFormat('dd.MM.yyyy') : '-'}
          </span>
        );
      },
      size: 130,
      minSize: 100,
      maxSize: 150,
    },
    {
      accessorKey: 'endDate',
      header: ({ column }) => <SortableHeader column={column} title={t('contracts.endDate')} />,
      cell: ({ row }) => {
        const date = row.getValue('endDate') as string;
        return (
          <span className="text-muted-foreground">
            {date ? DateTime.fromISO(date).toFormat('dd.MM.yyyy') : '-'}
          </span>
        );
      },
      size: 130,
      minSize: 100,
      maxSize: 150,
    },
    {
      accessorKey: 'status',
      header: ({ column }) => <SortableHeader column={column} title={t('contracts.statusLabel')} />,
      cell: ({ row }) => {
        const status = row.getValue('status') as 'open' | 'closed';
        return (
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${
              status === 'closed'
                ? 'bg-muted text-muted-foreground border-border'
                : 'bg-success-subtle text-success-subtle-foreground border-transparent'
            }`}
          >
            <span
              className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
                status === 'closed' ? 'bg-muted-foreground' : 'bg-success-subtle-foreground'
              }`}
            />
            {status === 'closed' ? t('contracts.status.closed') : t('contracts.status.open')}
          </span>
        );
      },
      size: 120,
      minSize: 100,
      maxSize: 150,
    },
    {
      accessorKey: 'createdAt',
      header: ({ column }) => <SortableHeader column={column} title={t('common.createdAt')} />,
      cell: ({ row }) => {
        const date = row.getValue('createdAt') as string;
        return (
          <span className="text-muted-foreground">
            {DateTime.fromISO(date).toFormat('dd.MM.yyyy')}
          </span>
        );
      },
      size: 130,
      minSize: 100,
      maxSize: 150,
    },
    {
      id: 'actions',
      header: () => <span className="sr-only">{t('common.actions')}</span>,
      cell: ({ row }) => (
        <ActionCell
          contract={row.original}
          onDelete={setContractToDelete}
          onCopy={setContractToCopy}
          onViewDetail={handleViewDetail}
          basePath={basePath}
          canUpdate={canUpdateContracts}
          canCopy={canCreateContracts}
          canDelete={canDeleteContracts}
        />
      ),
      size: 80,
      minSize: 60,
      maxSize: 100,
    },
  ];

  const { data: contractsData, isLoading } = useContracts({
    page: view === 'table' ? pagination.pageIndex + 1 : 1,
    limit: view === 'table' ? pagination.pageSize : 0,
    status: selectedStatus,
    where: {
      and: [
        ...(showSubcontracts
          ? []
          : [
              {
                parentContract: {
                  exists: false,
                },
              },
            ]),
        ...(debouncedSearchTerm
          ? [
              {
                or: [
                  {
                    orderNumber: {
                      like: debouncedSearchTerm,
                    },
                  },
                  {
                    title: {
                      like: debouncedSearchTerm,
                    },
                  },
                ],
              },
            ]
          : []),
        ...(selectedCustomerId && canReadCustomers
          ? [
              {
                customer: {
                  equals: selectedCustomerId,
                },
              },
            ]
          : []),
        ...(selectedObjectId && canReadObjects
          ? [
              {
                aggregatedObjects: {
                  in: [selectedObjectId],
                },
              },
            ]
          : []),
      ],
    },
    sort: getSortParam<Contract>(sorting, {
      ...(canReadCustomers ? { customer: 'customer.name' } : {}),
    }),
  });

  const [contractToDelete, setContractToDelete] = useState<Contract | null>(null);
  const [contractToCopy, setContractToCopy] = useState<Contract | null>(null);
  const deleteContract = useDeleteContract();
  const totalDocs = contractsData?.totalDocs || 0;

  const handleDeleteContract = async () => {
    if (contractToDelete) {
      try {
        await deleteContract.mutateAsync(contractToDelete.id);
        toast.success(t('contracts.contractDeletedSuccessfully'));
        setContractToDelete(null);
      } catch (_error) {
        toast.error(t('contracts.errorDeletingContract'));
      }
    }
  };

  const table = useReactTable({
    data: contractsData?.docs ?? [],
    columns,
    pageCount: contractsData?.totalPages ?? 0,
    rowCount: totalDocs,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      pagination,
    },
    onPaginationChange: setPagination,
    manualPagination: true,
    manualSorting: true,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    columnResizeMode: 'onChange',
  });

  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  const timelineRef = useRef<ContractsTimelineHandle>(null);

  const layoutActions = (
    <div className="flex flex-wrap items-center justify-end gap-2">
      {view === 'timeline' ? (
        <TimelineControls
          onExpandAll={() => timelineRef.current?.handleExpandAll()}
          onCollapseAll={() => timelineRef.current?.handleCollapseAll()}
          onZoomIn={() => timelineRef.current?.handleZoomIn()}
          onZoomOut={() => timelineRef.current?.handleZoomOut()}
        />
      ) : null}

      <ToggleGroup
        type="single"
        value={view}
        onValueChange={(value) => {
          if (!value) return;
          setView(value as 'table' | 'timeline');
        }}
        className="gap-0 rounded-md border border-input bg-background overflow-hidden shadow-xs"
        aria-label={t('common.view')}
      >
        <ToggleGroupItem
          value="table"
          title={t('common.tableView')}
          className="rounded-none border-r border-input data-[state=off]:text-muted-foreground hover:text-foreground data-[state=on]:shadow-sm"
          aria-label={t('common.tableView')}
          data-testid="contracts-view-table"
        >
          <Table2 className="size-4" />
        </ToggleGroupItem>
        <ToggleGroupItem
          value="timeline"
          title={t('common.timelineView')}
          className="rounded-none data-[state=off]:text-muted-foreground hover:text-foreground data-[state=on]:shadow-sm"
          aria-label={t('common.timelineView')}
          data-testid="contracts-view-timeline"
        >
          <ChartGantt className="size-4" />
        </ToggleGroupItem>
      </ToggleGroup>
    </div>
  );

  const hasActiveFilters = Boolean(
    searchTerm ||
      selectedCustomerId ||
      (canReadObjects && selectedObjectId) ||
      selectedStatus ||
      showSubcontracts === true
  );

  const handleClearAllFilters = useCallback(() => {
    setSearchTerm('');
    setSelectedCustomerId(undefined);
    setSelectedObjectId(undefined);
    setSelectedStatus(undefined);
    setShowSubcontracts(false);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  const activeFilterTags = (() => {
    const tags: Array<{ id: string; label: ReactNode; onRemove: () => void }> = [];
    if (searchTerm) {
      tags.push({
        id: 'search',
        label: (
          <>
            <span className="font-medium text-foreground">{t('common.searchLabel')}:</span>
            <span className="truncate max-w-[240px]" title={searchTerm}>
              {searchTerm}
            </span>
          </>
        ),
        onRemove: () => handleSearchChange(''),
      });
    }

    if (selectedStatus) {
      tags.push({
        id: 'status',
        label: (
          <>
            <span className="font-medium text-foreground">{t('contracts.statusLabel')}:</span>
            <span>
              {selectedStatus === 'closed'
                ? t('contracts.status.closed')
                : t('contracts.status.open')}
            </span>
          </>
        ),
        onRemove: () => {
          setSelectedStatus(undefined);
          setPagination((prev) => ({ ...prev, pageIndex: 0 }));
        },
      });
    }

    if (selectedCustomerId && canReadCustomers) {
      tags.push({
        id: 'customer',
        label: (
          <>
            <span className="font-medium text-foreground">{t('customers.customer')}:</span>
            <CustomerFilterLabel customerId={selectedCustomerId} />
          </>
        ),
        onRemove: () => {
          setSelectedCustomerId(undefined);
          setPagination((prev) => ({ ...prev, pageIndex: 0 }));
        },
      });
    }

    if (selectedObjectId && canReadObjects) {
      tags.push({
        id: 'object',
        label: (
          <>
            <span className="font-medium text-foreground">{t('objects.object')}:</span>
            <ObjectFilterLabel objectId={selectedObjectId} />
          </>
        ),
        onRemove: () => {
          setSelectedObjectId(undefined);
          setPagination((prev) => ({ ...prev, pageIndex: 0 }));
        },
      });
    }

    if (showSubcontracts) {
      tags.push({
        id: 'subcontracts',
        label: <span className="font-medium text-foreground">{t('contracts.subcontracts')}</span>,
        onRemove: () => {
          setShowSubcontracts(false);
          setPagination((prev) => ({ ...prev, pageIndex: 0 }));
        },
      });
    }

    return tags;
  })();

  return (
    <PageContainer>
      <TooltipProvider>
        <PageTitle
          icon={FileText}
          breadcrumb={
            <>
              <SimpleBreadcrumbItem>{t('contracts.contracts')}</SimpleBreadcrumbItem>
              <SimpleBreadcrumbItem isLast>
                {t('navigation.items.allContracts')}
              </SimpleBreadcrumbItem>
            </>
          }
          title={t('contracts.manageContracts')}
          primaryAction={{
            label: t('contracts.addContract'),
            onClick: () => navigate(`${basePath}/new`),
            icon: <Plus size={16} />,
            disabled: !canCreateContracts,
          }}
        />

        <TableCard className="mb-4">
          <TableCardHeader>
            <TableSearchBar
              searchTerm={searchTerm}
              onSearchChange={handleSearchChange}
              placeholder={t('contracts.searchPlaceholder')}
              actions={layoutActions}
            />
          </TableCardHeader>
        </TableCard>

        <div className="mb-4 space-y-3">
          <TableFiltersBar
            clearAll={
              hasActiveFilters ? (
                <TableClearAllButton
                  onClick={handleClearAllFilters}
                  testId="contracts-filters-clear-all"
                />
              ) : undefined
            }
          >
            <Toggle
              variant="outline"
              data-testid="contracts-show-subcontracts"
              aria-label={t('contracts.showSubcontracts')}
              pressed={showSubcontracts}
              onPressedChange={(pressed) => {
                setShowSubcontracts(pressed);
                setPagination((prev) => ({ ...prev, pageIndex: 0 }));
              }}
              className="h-9 px-3 bg-background text-muted-foreground data-[state=on]:bg-accent data-[state=on]:text-accent-foreground data-[state=on]:shadow-xs"
            >
              <GitBranch className="size-4" />
              <span className="hidden md:inline">{t('contracts.subcontracts')}</span>
            </Toggle>

            <ToggleGroup
              type="single"
              value={selectedStatus ?? 'all'}
              onValueChange={(value) => {
                if (!value) return;
                setSelectedStatus(value === 'all' ? undefined : (value as 'open' | 'closed'));
                setPagination((prev) => ({ ...prev, pageIndex: 0 }));
              }}
              className="gap-0 rounded-md border border-input bg-background overflow-hidden shadow-xs"
              aria-label={t('contracts.statusFilter')}
            >
              <ToggleGroupItem
                value="all"
                className="rounded-none border-r border-input data-[state=off]:text-muted-foreground hover:text-foreground data-[state=on]:shadow-sm"
                aria-label={t('common.all')}
              >
                <Circle className="size-4" />
                <span className="hidden sm:inline">{t('common.all')}</span>
              </ToggleGroupItem>
              <ToggleGroupItem
                value="open"
                className="rounded-none border-r border-input data-[state=off]:text-muted-foreground hover:text-foreground data-[state=on]:shadow-sm"
                aria-label={t('contracts.status.open')}
              >
                <CircleDot className="size-4" />
                <span className="hidden sm:inline">{t('contracts.status.open')}</span>
              </ToggleGroupItem>
              <ToggleGroupItem
                value="closed"
                className="rounded-none data-[state=off]:text-muted-foreground hover:text-foreground data-[state=on]:shadow-sm"
                aria-label={t('contracts.status.closed')}
              >
                <CircleCheck className="size-4" />
                <span className="hidden sm:inline">{t('contracts.status.closed')}</span>
              </ToggleGroupItem>
            </ToggleGroup>

            {canReadCustomers && (
              <SearchCombobox
                value={selectedCustomerId}
                onSelect={(value) => {
                  setSelectedCustomerId(value || undefined);
                  setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                }}
                collectionKey="customers"
                searchKey="name"
                className="h-9 w-full sm:w-[220px] bg-background"
                texts={{ selectItemMsg: t('customers.customer') }}
                icon={Building2}
              />
            )}
            {canReadObjects ? (
              <SearchCombobox
                value={selectedObjectId}
                onSelect={(value) => {
                  setSelectedObjectId(value || undefined);
                  setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                }}
                collectionKey="objects"
                searchKey="name"
                className="h-9 w-full sm:w-[220px] bg-background"
                texts={{ selectItemMsg: `${t('objects.object')}` }}
                icon={Box}
              />
            ) : null}
          </TableFiltersBar>

          <TableActiveFilterTags tags={activeFilterTags} />
        </div>

        <TableCard className="mb-6">
          {view === 'table' ? (
            <>
              <TableCardContent>
                <Table>
                  <TableHeader>
                    {table.getHeaderGroups().map((headerGroup) => (
                      <TableHeaderRow key={headerGroup.id}>
                        {headerGroup.headers.map((header, index) => (
                          <TableHead
                            key={header.id}
                            style={{ width: `${header.getSize()}px` }}
                            className={
                              index === 0
                                ? 'pl-6'
                                : index === headerGroup.headers.length - 1
                                  ? 'pr-6 text-right'
                                  : ''
                            }
                          >
                            {header.isPlaceholder
                              ? null
                              : flexRender(header.column.columnDef.header, header.getContext())}
                          </TableHead>
                        ))}
                      </TableHeaderRow>
                    ))}
                  </TableHeader>
                  <TableBody>
                    {isLoading ? (
                      <TableLoadingCell colSpan={columns.length} />
                    ) : table.getRowModel().rows?.length ? (
                      table.getRowModel().rows.map((row) => (
                        <TableRow
                          key={row.id}
                          data-state={row.getIsSelected() && 'selected'}
                          className="cursor-pointer"
                          onClick={() => handleViewDetail(row.original.id)}
                        >
                          {row.getVisibleCells().map((cell, index) => (
                            <TableCell
                              key={cell.id}
                              style={{ width: `${cell.column.getSize()}px` }}
                              className={
                                index === 0
                                  ? 'pl-6'
                                  : index === row.getVisibleCells().length - 1
                                    ? 'pr-6'
                                    : ''
                              }
                            >
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : (
                      <EmptyTableRow colSpan={columns.length} />
                    )}
                  </TableBody>
                </Table>
              </TableCardContent>

              <TableCardFooter>
                <TablePagination table={table} />
              </TableCardFooter>
            </>
          ) : (
            <div>
              {contractsData?.docs && contractsData?.docs.length > 0 ? (
                <ContractsTimeline
                  ref={timelineRef}
                  contracts={contractsData?.docs || []}
                  groupByCustomer
                  onDelete={canDeleteContracts ? setContractToDelete : undefined}
                  onCopy={canCreateContracts ? setContractToCopy : undefined}
                />
              ) : (
                <div className="p-8 text-center text-muted-foreground text-sm">
                  {t('common.noResults')}
                </div>
              )}
            </div>
          )}
        </TableCard>

        <AlertDialog
          open={!!contractToDelete}
          onOpenChange={(open) => !open && setContractToDelete(null)}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('contracts.deleteContract')}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('contracts.deleteContractConfirmation', {
                  name: contractToDelete?.orderNumber || '',
                })}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
              <AlertDialogAction onClick={handleDeleteContract}>
                {t('common.delete')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <CopyContractModal
          contract={contractToCopy}
          open={!!contractToCopy}
          onOpenChange={(open) => !open && setContractToCopy(null)}
        />
      </TooltipProvider>
    </PageContainer>
  );
}
